import { useTranslation } from 'react-i18next';
import styles from './Faq.module.css';
import FaqBtn from '../FaqBtn';
import { useContext, useState } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { FAQDTO } from '@/openapi';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';

interface FAQProps {
	id?: number;
	question: string;
	answer: string;
	category: string;
	updatable: boolean;
	onDelete?: (id: number) => void;
	onEdit?: (faq: FAQDTO) => void;
}

const Faq = (props: FAQProps) => {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showEditConfirm, setShowEditConfirm] = useState(false);

	const toggleFaq = () => {
		setIsOpen((prev) => !prev);
	};

	const authCtx = useContext(AuthContext);
	const isRoleAdmin = authCtx.role?.some((r) => r.name === 'ROLE_ADMIN');

	const isUpdatable = () => {
		if (authCtx.isLoggedIn && isRoleAdmin && props.updatable) return true;

		// Default fallback
		return false;
	};

	// UPDATE function
	const toggleEditFAQ = () => {
		setShowEditConfirm(true);
	};

	const confirmEdit = () => {
		if (props.id && props.onEdit && isRoleAdmin) {
			props.onEdit({
				id: props.id,
				question: props.question,
				answer: props.answer,
				category: props.category as FAQDTO['category']
			});
		}
		setShowEditConfirm(false);
	};

	const cancelEdit = () => {
		setShowEditConfirm(false);
	};

	// DELETE function
	const toggleDeleteFAQ = () => {
		setShowDeleteConfirm(true);
	};

	const confirmDelete = () => {
		if (props.id && props.onDelete && isRoleAdmin) {
			props.onDelete(props.id);
		}
		setShowDeleteConfirm(false);
	};

	const cancelDelete = () => {
		setShowDeleteConfirm(false);
	};

	return (
		<div className={styles.single_faq_container}>
			<div className={styles.question_container}>
				<div className={styles.domanda}>{props.question}</div>
				<div className={styles.faqBtnsContainer}>
					<FaqBtn onClick={toggleFaq} isOpen={isOpen} type="expand" />

					{isUpdatable() && (
						<>
							<FaqBtn onClick={toggleEditFAQ} type="edit" />
							<FaqBtn onClick={toggleDeleteFAQ} type="delete" />
						</>
					)}
				</div>
			</div>

			{isOpen && (
				<div className={styles.risposta}>
					<hr></hr>
					{props.answer}
				</div>
			)}

			<ConfirmationDialog
				isOpen={showDeleteConfirm}
				title={t('common.deleteOp')}
				message={t('adminPage.faqSection.deleteConfirmation')}
				confirmText={t('common.yes')}
				cancelText={t('common.no')}
				onConfirm={confirmDelete}
				onCancel={cancelDelete}
			/>

			<ConfirmationDialog
				isOpen={showEditConfirm}
				title={t('common.updateOp')}
				message={t('adminPage.faqSection.editInfo')}
				confirmText={t('common.ok')}
				cancelText={t('common.cancel')}
				onConfirm={confirmEdit}
				onCancel={() => setShowEditConfirm(false)}
			/>
		</div>
	);
};

export default Faq;
